import { Link, useParams } from 'react-router-dom';

import { getErrorMessage } from '@/hooks/useApi';

import { useStory } from './useStories';

export function StoryDetailPage() {
  const { storyId } = useParams<{ storyId: string }>();
  const { data: story, isLoading, error } = useStory(storyId);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading story...</p>
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg bg-red-50 p-4 text-red-700">{getErrorMessage(error)}</div>;
  }

  if (!story) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Story not found</p>
        <Link to="/stories" className="mt-4 text-indigo-600 hover:text-indigo-800">
          Back to stories
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link to="/stories" className="text-indigo-600 hover:text-indigo-800">
          &larr; Back to Stories
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{story.title}</h1>
          <p className="mt-2 text-slate-600">{story.description}</p>
        </div>
        <div className="flex items-center gap-3">
          {story.status === 'ready' && (
            <Link
              to={`/stories/${storyId}/free-content`}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
            >
              Manage Free Content
            </Link>
          )}
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <DetailCard label="Story ID" value={story.storyId} />
        <DetailCard label="Genre" value={story.genre} />
        <DetailCard label="Root Node ID" value={story.rootNodeId || 'Not generated'} />
        <DetailCard label="Total Nodes" value={story.totalNodes.toString()} />
        <DetailCard label="Total Plays" value={story.totalPlays.toString()} />
        <DetailCard label="Created" value={new Date(story.createdAt).toLocaleDateString()} />
      </div>

      {/* Status display */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Story Status</h2>
        <div className="flex items-center gap-4">
          <StatusBadge status={story.status} />
        </div>
        <p className="mt-4 text-sm text-slate-500">
          <strong>Initializing:</strong> Root node is being generated. <strong>Ready:</strong> Story
          is playable. <strong>Failed:</strong> Generation failed.
        </p>
      </div>
    </div>
  );
}

function DetailCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <dt className="text-sm font-medium text-slate-500">{label}</dt>
      <dd className="mt-1 text-lg text-slate-900 truncate" title={value}>
        {value}
      </dd>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    initializing: 'bg-yellow-100 text-yellow-700',
    ready: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={`inline-block rounded-full px-4 py-2 text-sm font-medium ${colors[status] || 'bg-slate-100 text-slate-700'}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
